import { animate, AnimationBuilder, AnimationFactory, AnimationMetadata } from '@angular/animations';
import { Injectable, Renderer2 } from '@angular/core';
import { Subject } from 'rxjs';

import { INgAnimationParams } from '../event-animation.interface';
import { AbstractNgAnimationService } from '../animation-service.abstract';
import { NG_EVENT_TRIGGERS, INgEventTriggers } from '../event-animation.data';


/**------------------------------------------------------
 * Static Animation
 * ----------------
 * > The animation goes on from the start event till the
 * > animation duration is finished. Both the start and
 * > end event are triggering a short animation. Therefor
 * > there are two animations configured for static animations.
 */
@Injectable()
export class NgStaticAnimationService extends AbstractNgAnimationService {

	//** Helper Variables */
	private startListener$	: Subject<void> = new Subject<void>();
	private endListener$	: Subject<void> = new Subject<void>();

	constructor(
		private animationBuilder	: AnimationBuilder,
		private renderer			: Renderer2
	) {
		super();
	}


	/**------------------------------------------------------
	 * Setup the Animation
	 */
	setupAnimation(params: INgAnimationParams): void {

		//0 - define the options
		params = this.verifyParameters(params);

		//1 - get triggers for the provided event
		const triggers				: INgEventTriggers 	= NG_EVENT_TRIGGERS[params.event];
		const startAnimationMetadata: AnimationMetadata = animate(`${params.timing.duration}ms ${params.timing.delay}ms`, params.states.end);
		const stopAnimationMetadata : AnimationMetadata = animate(`${params.timing.duration}ms ${params.timing.delay}ms`, params.states.start);

		//2 - listen and animate with start animation metadata on start event
		this.startListener$.subscribe(() => {
			const unListenStartEvent: Function = this.renderer.listen(params.triggerElement, triggers.start, () => {
				unListenStartEvent();
				this.endListener$.next();
				this.animateEffect(params.targetElement, [startAnimationMetadata]);
			});
		});

		//3 - listen and animate with start animation metadata on start event
		this.endListener$.subscribe(() => {
			const unListenEndEvent: Function = this.renderer.listen(params.triggerElement, triggers.end, () => {
				unListenEndEvent();
				this.startListener$.next();
				this.animateEffect(params.targetElement, [stopAnimationMetadata]);
			});
		});

		//4 - animate with start event
		this.startListener$.next();
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private animateEffect(targetElement: HTMLElement, animationMetadata: AnimationMetadata[]) {

		//0 - if a previous one exist, destroy it
		this.animationPlayer?.destroy();

		//1 - create the animation player
		const animationFactory: AnimationFactory = this.animationBuilder.build(animationMetadata);
		this.animationPlayer = animationFactory.create(targetElement);

		//2 - animate the effect
		this.animationPlayer.play();
	}

	protected verifyCustomParameters(params: INgAnimationParams): INgAnimationParams {
		if (!params?.states?.start) throw new Error(`NgStaticAnimationService => verifyParameters => FATAL ERROR: The animation states are required for defining the animation behavior, the starting state is undefined (start animation state: ${params?.states?.start})`);
		if (!params?.states?.end) 	throw new Error(`NgStaticAnimationService => verifyParameters => FATAL ERROR: The animation states are required for defining the animation behavior, the ending state is undefined (end animation state: ${params?.states?.end})`);
		return params;
	}
}
